import React from "react";
import { useForm } from "react-hook-form";
import { Badge, Card, CardBody, Col, Modal, ModalBody, Row, Table } from "reactstrap";

import { AvatarIcon } from "@/components/Common/AvatarIcon";
import { EmailLink } from "@/components/Common/EmailLink";
import FileUploadInput from "@/components/Common/FileUploadInput";
import { MobileLabel } from "@/components/Common/MobileLabel";
import useCustomHook from "@/components/Hooks/useCustomHook";
import useUploadFileCover from "@/components/Hooks/useUploadFileCover";
import { formatDate } from "@/helpers/date_helper";
import { getCurrentUser } from "@/helpers/session_helper";

import UserLogsList from "./Components/UserLogsList";

import { useFindUsersQuery, useUpdateAvatarMutation } from "@/api/Endpoints/Core/Security/Users";

const profileBg = require(`@/${process.env.REACT_APP_CUSTOMER_USER_PROFILE}`);

const UserDetails = ({ data: userData, userId, show, onCloseClick }) => {
  const { state, customFunction } = useCustomHook();
  const [updateAvatarPhoto] = useUpdateAvatarMutation();
  const userIdParam = userId || userData?.userId;

  const showAvatarInput = getCurrentUser()?.userId === userIdParam;

  const { data, isFetching, refetch } = useFindUsersQuery(
    { userId: userIdParam },
    {
      skip: !userIdParam,
      refetchOnMountOrArgChange: true,
    },
  );

  const userInfo = data?.returnData;

  const {
    renderPreview: renderAvatarPreview,
    setImageValue: setAvatar,
    handleImageError: handleAvatarError,
  } = useUploadFileCover(userInfo?.avatar, userInfo?.avatar);

  const { control } = useForm();

  const handleAvatarUpload = async (file) => {
    if (file) {
      setAvatar(file);
      try {
        const uploaded = await updateAvatarPhoto({
          userId: userIdParam,
          AvatarFile: file,
        });

        if (uploaded && uploaded?.data?.returnData?.avatar) {
          const currentUser = JSON.parse(sessionStorage.getItem("currentUser")) || {};

          if (currentUser.userId === userIdParam) {
            currentUser.avatar = uploaded?.data?.returnData?.avatar;
            sessionStorage.setItem("currentUser", JSON.stringify(currentUser));
            window.dispatchEvent(new Event("CURRENTUSER_AVATAR_UPDATED"));
          }
        }
      } catch (error) {
        console.error("Error uploading avatar:", error);
      }
    }
  };

  const handleExpand = () => {
    customFunction.updateState({ toggleExpand: !state.toggleExpand });
  };

  return (
    <React.Fragment>
      <Modal
        fullscreen={state.toggleExpand}
        modalClassName="flip"
        id="userDetails"
        size="xl"
        isOpen={show}
        toggle={onCloseClick}
        centered
        backdrop="static">
        <ModalBody>
          <div>
            <div className="profile-foreground position-relative mx-n4 mt-n4">
              <div className="profile-wid-bg">
                <picture>
                  <source srcSet={profileBg} type="image/jpeg" />
                  <img src={profileBg} alt="" className="profile-wid-img" />
                </picture>
              </div>
            </div>
            <div className="pt-4 mb-4 mb-lg-3 pb-lg-4">
              <Row className="g-4">
                <div className="col-auto">
                  <div className="position-relative d-inline-block">
                    <div className="position-absolute bottom-0 end-0">
                      {showAvatarInput && (
                        <label htmlFor="avatarPhotoFile" className="mb-0" data-bs-toggle="tooltip" data-bs-placement="right" title="Select Image">
                          <div className="avatar-xs">
                            <div className="avatar-title bg-light border rounded-circle text-muted cursor-pointer">
                              <i className="ri-image-fill"></i>
                            </div>
                          </div>
                        </label>
                      )}
                      <FileUploadInput name="avatarPhotoFile" control={control} onFileSelect={handleAvatarUpload} />
                    </div>
                    <div className={`${renderAvatarPreview ? "avatar-lg avatar-title bg-light rounded-circle" : ""}`}>
                      {renderAvatarPreview ? (
                        <img
                          src={renderAvatarPreview}
                          onError={handleAvatarError}
                          className="img-thumbnail rounded-circle"
                          style={{ height: "86px", width: "86px" }}
                        />
                      ) : (
                        <AvatarIcon name={userInfo?.fullName} avatarImg={userInfo?.avatar} width="80px" height="80px" fontSize="30px" />
                      )}
                    </div>
                  </div>
                </div>
                <Col>
                  <div>
                    <h3 className="text-white mb-1">{userInfo?.fullName}</h3>
                    <div className="hstack text-white-50 gap-1">
                      <div>
                        <i className="ri-building-line me-1 text-white text-opacity-75 fs-16 align-middle"></i>
                        {process.env.REACT_APP_CUSTOMER_FULL_NAME}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col xs={12} className="col-lg-auto order-last order-lg-0">
                  <Row className="text text-white-50 text-center">
                    <Col lg={6} xs={12} className="ps-0 mr-0 pr-0">
                      <div>
                        <button type="button" className="btn btn-sm " onClick={handleExpand} title="Expand">
                          {state.toggleExpand ? (
                            <i className="ri-fullscreen-exit-line text-white text-bold" style={{ fontSize: "14px" }}></i>
                          ) : (
                            <i className="ri-fullscreen-line text-bold text-white " style={{ fontSize: "14px" }}></i>
                          )}
                        </button>
                      </div>
                    </Col>
                    <Col lg={6} xs={12} className="ps-0">
                      <div className="hstack gap-1 flex-wrap mt-4 mt-md-0">
                        <button type="button" className="btn btn-icon btn-sm btn-ghost-danger fs-16" onClick={onCloseClick}>
                          X
                        </button>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>

            <Row>
              <Col xxl={4}>
                <Card className="mb-0">
                  <CardBody>
                    <div className="d-flex align-items-center mb-4">
                      <h5 className="card-title flex-grow-1 mb-0" style={{ marginTop: "10px" }}>
                        User Info
                      </h5>
                    </div>
                    <div className="table-responsive">
                      <Table className="table-borderless mb-0">
                        <tbody>
                          <tr>
                            <th className="ps-0" scope="row">
                              User Name :
                            </th>
                            <td>{userInfo?.userName}</td>
                          </tr>
                          <tr>
                            <th className="ps-0" scope="row">
                              Full Name :
                            </th>
                            <td>{userInfo?.fullName}</td>
                          </tr>
                          <tr>
                            <th className="ps-0" scope="row">
                              Mobile :
                            </th>
                            <td>
                              <MobileLabel value={userInfo?.mobileNo} />
                            </td>
                          </tr>
                          <tr>
                            <th className="ps-0" scope="row">
                              Email :
                            </th>
                            <td>
                              <EmailLink email={userInfo?.emailAddress} />
                            </td>
                          </tr>
                          <tr>
                            <th className="ps-0" scope="row">
                              Date Joined :
                            </th>
                            <td>{formatDate(userInfo?.createdDate)}</td>
                          </tr>
                          <tr>
                            <th className="ps-0" scope="row">
                              Status :
                            </th>
                            <td>{userInfo?.statusName}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody>
                    <h5 className="card-title mb-4">Roles</h5>
                    <div className="d-flex flex-wrap gap-2 fs-15">
                      <>
                        {userInfo?.roles.map((role, index) => (
                          <span key={index}>
                            <Badge color="info">{role.roleName}</Badge>
                            {"  "}
                          </span>
                        ))}
                      </>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col xxl={8}>{userIdParam && <UserLogsList userId={userIdParam} />}</Col>
            </Row>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default UserDetails;
